import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import theme from '../utils/theme';
import MainBanner from './common/MainBanner';

export default function LoanCalculator() {
	const [principal, setPrincipal] = useState(1000); // 만원 단위
	const [term, setTerm] = useState(120); // 개월 단위
	const [interestRate, setInterestRate] = useState(6.9); // 연이율 %
	const [monthlyPayment, setMonthlyPayment] = useState(0);

	// 월 상환금액 계산 함수
	const calculateMonthlyPayment = () => {
		const principalAmount = principal * 10000; // 만원을 원으로 변환
		const monthlyRate = interestRate / 100 / 12; // 연이율을 월이율로 변환

		if (monthlyRate === 0) {
			setMonthlyPayment(principalAmount / term);
			return;
		}

		// 원리금 균등상환 공식
		const payment = principalAmount * (monthlyRate * Math.pow(1 + monthlyRate, term)) / (Math.pow(1 + monthlyRate, term) - 1);
		setMonthlyPayment(Math.round(payment));
	};

	// 입력값이 변경될 때마다 계산 실행
	useEffect(() => {
		calculateMonthlyPayment();
	}, [principal, term, interestRate]);

	const formatNumber = (num) => {
		return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
	};

	return (
		<MainBanner>
			<BannerDiv>
				<CalculatorCard>
					<CalculatorTitle>
						원리금계산기
						<TitleUnderline />
					</CalculatorTitle>

					<InputSection>
						<InputRow>
							<InputField>
								<InputValue
									type="number"
									value={principal}
									onChange={(e) => setPrincipal(Number(e.target.value))}
									min="1"
									max="10000"
								/>
								<InputUnit>만원</InputUnit>
							</InputField>
							<InputLabel>을 총</InputLabel>
						</InputRow>

						<InputRow>
							<InputField>
								<InputValue
									type="number"
									value={term}
									onChange={(e) => setTerm(Number(e.target.value))}
									min="1"
									max="360"
								/>
								<InputUnit>개월</InputUnit>
							</InputField>
							<InputLabel>에 걸쳐</InputLabel>
						</InputRow>

						<InputRow>
							<InputField>
								<InputValue
									type="number"
									value={interestRate}
									onChange={(e) => setInterestRate(Number(e.target.value))}
									min="0.1"
									max="30"
									step="0.1"
								/>
								<InputUnit>%</InputUnit>
							</InputField>
							<InputLabel>의 금리로</InputLabel>
						</InputRow>
					</InputSection>

					<QuestionText>매달 일정한 금액을 상환한다면?</QuestionText>
					<Separator />

					<ResultSection>
						<ResultLabel>월 상환금액은</ResultLabel>
						<ResultBox>
							<ResultNote>이자포함</ResultNote>
							<ResultAmount>{formatNumber(monthlyPayment)}</ResultAmount>
							<ResultUnit>원</ResultUnit>
						</ResultBox>
						<ResultEnd>입니다.</ResultEnd>
					</ResultSection>

					<DisclaimerSection>
						<DisclaimerItem>
							<InfoIcon>i</InfoIcon>
							<DisclaimerText>
								납입금은 원리금 균등상환으로 계산되었으며, 금리 할인 혜택은 포함되어 있지 않습니다.
							</DisclaimerText>
						</DisclaimerItem>
						<DisclaimerItem>
							<InfoIcon>i</InfoIcon>
							<DisclaimerText>
								정확한 개인별 한도 및 금리는 한도조회를 통해 확인하실 수 있으며, 한도조회 시 개인신용평점에는 영향이 없습니다.
							</DisclaimerText>
						</DisclaimerItem>
					</DisclaimerSection>
				</CalculatorCard>
			</BannerDiv>
		</MainBanner>
	);
}

const BannerDiv = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: stretch;
	gap: 24px;
`;

const CalculatorCard = styled.div`
	background: white;
	border-radius: 16px;
	padding: 32px 24px;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
	max-width: 400px;
	margin: 0 auto;
	width: 100%;
	box-sizing: border-box;
	overflow: hidden;
`;

const CalculatorTitle = styled.h2`
	color: ${theme.navyColor};
	font-size: 24px;
	font-weight: 700;
	text-align: center;
	margin-bottom: 24px;
`;

const TitleUnderline = styled.div`
	width: 40px;
	height: 3px;
	background: ${theme.mainColor};
	margin: 12px auto 0;
	border-radius: 2px;
`;

const InputSection = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	margin-bottom: 20px;
`;

const InputRow = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	flex-wrap: wrap;
`;

const InputField = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
	background: white;
	border: 2px solid ${theme.mainColor};
	border-radius: 8px;
	padding: 8px 12px;
	min-width: 120px;
	max-width: 150px;
	flex-shrink: 0;
`;

const InputValue = styled.input`
	color: ${theme.mainColor};
	font-size: 16px;
	font-weight: 600;
	border: none;
	background: transparent;
	outline: none;
	width: 60px;
	text-align: right;
	
	&::-webkit-outer-spin-button,
	&::-webkit-inner-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}
	
	&[type=number] {
		-moz-appearance: textfield;
	}
`;

const InputUnit = styled.span`
	color: ${theme.navyColor};
	font-size: 14px;
	font-weight: 400;
`;

const InputLabel = styled.span`
	color: ${theme.navyColor};
	font-size: 14px;
	font-weight: 400;
`;

const QuestionText = styled.p`
	color: ${theme.navyColor};
	font-size: 16px;
	font-weight: 500;
	margin: 20px 0 16px;
`;

const Separator = styled.div`
	height: 1px;
	background: ${theme.gray30Color};
	margin: 16px 0;
`;

const ResultSection = styled.div`
	text-align: center;
	margin-bottom: 24px;
`;

const ResultLabel = styled.div`
	color: ${theme.navyColor};
	font-size: 16px;
	font-weight: 600;
	margin-bottom: 12px;
`;

const ResultBox = styled.div`
	background: white;
	border: 2px solid ${theme.mainColor};
	border-radius: 8px;
	padding: 16px;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	margin-bottom: 8px;
	flex-wrap: wrap;
	word-break: break-word;
`;

const ResultNote = styled.span`
	color: ${theme.gray60Color};
	font-size: 12px;
	font-weight: 400;
`;

const ResultAmount = styled.span`
	color: ${theme.mainColor};
	font-size: 24px;
	font-weight: 700;
`;

const ResultUnit = styled.span`
	color: ${theme.navyColor};
	font-size: 14px;
	font-weight: 400;
`;

const ResultEnd = styled.div`
	color: ${theme.navyColor};
	font-size: 14px;
	font-weight: 400;
`;

const DisclaimerSection = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
`;

const DisclaimerItem = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 8px;
`;

const InfoIcon = styled.div`
	width: 16px;
	height: 16px;
	border-radius: 50%;
	background: ${theme.mainColor};
	color: white;
	font-size: 12px;
	font-weight: 600;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	margin-top: 2px;
`;

const DisclaimerText = styled.p`
	color: ${theme.gray70Color};
	font-size: 12px;
	line-height: 1.4;
	margin: 0;
`; 